/**
 * Line Items · Op-Code Report — answers the question:
 *   "Show me every RO where brake pads (or ANY specific service) were
 *    declined last month, plus how much $ walked out the door."
 *
 * Aggregates every service_item across declines in the selected window.
 * Click any row to expand and see the actual customers. Real dealer tool.
 */

import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, TrendingDown, DollarSign, ChevronDown, ChevronRight, Wrench } from "lucide-react";

const WINDOWS = [
  { key: "7d",  label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "mtd", label: "This month" },
  { key: "ytd", label: "Year to date" },
  { key: "all", label: "All time" },
];

const CATEGORY_COLORS = {
  safety: "bg-red-600 text-white",
  repair: "bg-orange-600 text-white",
  maintenance: "bg-blue-600 text-white",
  cosmetic: "bg-purple-600 text-white",
  other: "bg-neutral-600 text-white",
};

function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

export default function LineItemsReportPage() {
  const [window, setWindow] = useState("mtd");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/metrics/line-items?window=${window}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not load line-items report.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const filtered = useMemo(() => {
    const items = data?.line_items || [];
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter(
      (r) => r.name.toLowerCase().includes(needle) || r.category.toLowerCase().includes(needle)
    );
  }, [data, q]);

  const summary = useMemo(() => {
    const items = data?.line_items || [];
    return {
      unique_services: items.length,
      total_value: items.reduce((s, r) => s + (r.total_value || 0), 0),
      total_count: items.reduce((s, r) => s + (r.count || 0), 0),
    };
  }, [data]);

  return (
    <DashboardShell>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 no-print">
        <div className="min-w-0">
          <div className="font-mono-label text-muted-foreground">Op-Code Drill-Down</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-1">Declines by Service</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Every specific service declined by every customer, grouped so you can see the
            money walking out the door by <b>op code</b>. Each customer appears <b>once
            per declined service</b> — most recent decline wins. Filter by any service —
            brake pads, timing belt, tire rotation — and see which customers you can chase.
          </p>
        </div>
        <div className="flex gap-2 sm:shrink-0">
          <select
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            className="h-10 w-full sm:w-auto rounded-md border border-input bg-background px-3 text-sm font-mono-label"
            data-testid="line-items-window"
          >
            {WINDOWS.map((w) => (
              <option key={w.key} value={w.key}>{w.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Card className="p-4 border-l-4 border-l-rose-500" data-testid="li-kpi-declines">
          <div className="font-mono-label text-[0.6rem] text-muted-foreground tracking-widest">
            DECLINES IN WINDOW
          </div>
          <div className="font-display font-black text-3xl tabular-nums text-navy mt-1">
            {data?.total_declines ?? 0}
          </div>
          <div className="text-[0.7rem] text-muted-foreground mt-1">customer decline events</div>
        </Card>
        <Card className="p-4 border-l-4 border-l-blue-600" data-testid="li-kpi-services">
          <div className="font-mono-label text-[0.6rem] text-muted-foreground tracking-widest">
            UNIQUE SERVICES DECLINED
          </div>
          <div className="font-display font-black text-3xl tabular-nums text-navy mt-1">
            {summary.unique_services}
          </div>
          <div className="text-[0.7rem] text-muted-foreground mt-1">
            {summary.total_count} total line items across all customers
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-[hsl(var(--gold))]" data-testid="li-kpi-total">
          <div className="font-mono-label text-[0.6rem] text-muted-foreground tracking-widest">
            $ WALKING OUT
          </div>
          <div className="font-display font-black text-3xl tabular-nums text-navy mt-1">
            {money(summary.total_value)}
          </div>
          <div className="text-[0.7rem] text-muted-foreground mt-1">
            total declined-service value in window
          </div>
        </Card>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={2} />
        <Input
          placeholder="Search: brake, tire, timing belt, oil…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-10"
          data-testid="line-items-search"
        />
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Service · Op Code Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Declines</TableHead>
              <TableHead className="text-right">Avg $</TableHead>
              <TableHead className="text-right">Total $ Walking Out</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {busy && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!busy && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {q ? `No services match "${q}"` : "No declined services in this window yet."}
                </TableCell>
              </TableRow>
            )}
            {!busy &&
              filtered.map((row) => {
                const isOpen = expanded === row.name;
                return (
                  <>
                    <TableRow
                      key={row.name}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setExpanded(isOpen ? null : row.name)}
                      data-testid={`li-row-${row.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <TableCell className="w-10">
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" strokeWidth={2.2} />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" strokeWidth={2.2} />
                        )}
                      </TableCell>
                      <TableCell className="font-semibold">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={2} />
                          {row.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[0.6rem] font-mono-label tracking-widest ${
                            CATEGORY_COLORS[row.category] || CATEGORY_COLORS.other
                          }`}
                        >
                          {row.category?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-bold">{row.count}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {money(row.avg_value)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-bold text-red-700">
                        {money(row.total_value)}
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-muted/30" data-testid={`li-detail-${row.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <TableCell colSpan={6} className="p-0">
                          <div className="p-4 border-t border-border">
                            <div className="font-mono-label text-[0.6rem] tracking-widest text-muted-foreground mb-2">
                              CUSTOMERS WHO DECLINED THIS ({row.customers.length} of {row.count})
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {row.customers.map((c, i) => (
                                <div
                                  key={c.decline_id + i}
                                  className="flex items-center justify-between gap-3 border border-border rounded-md p-2 bg-background"
                                >
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm truncate">
                                      {c.customer_name}
                                    </div>
                                    <div className="text-[0.65rem] text-muted-foreground truncate">
                                      {c.vehicle || "—"} · <span data-testid={`li-status-${c.decline_id}`}>{c.status_label || c.status || "—"}</span>
                                    </div>
                                  </div>
                                  <div className="font-display font-bold tabular-nums text-red-700 shrink-0">
                                    {money(c.price)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
          </TableBody>
        </Table>
      </Card>
    </DashboardShell>
  );
}
